const { locationStorage } = require('app_honda_cars/utils/locationStorage');
const { tabIdentity } = require('app_honda_cars/utils/tabIdentity');

const BREAKPOINTS = {
    sm: 441,
    md: 1024,
    lg: 1440
}

/**
 * Get cookie value by cookie name from browser.
 * Under tab isolation, `userloc` is resolved via locationStorage (per-tab).
 * @param {string} cookieName - name of the cookie
 * @returns {string} cookie value of the found cookie name
 */
function getCookie(cookieName) {
    if (cookieName === 'userloc' && tabIdentity.isTabIsolation()) {
        return locationStorage.get() || '';
    }

    var name = cookieName + '=';
    var decodedCookie = decodeURIComponent(document.cookie);
    var cookieArray = decodedCookie.split(';');

    for (var i = 0; i < cookieArray.length; i++) {
        var cookieItem = cookieArray[i];

        while (cookieItem.charAt(0) === ' ') {
            cookieItem = cookieItem.substring(1);
        }

        if (cookieItem.indexOf(name) === 0) {
            return cookieItem.substring(name.length, cookieItem.length);
        }
    }

    return '';
}

/**
 * Set a cookie with a specified name, value, and options.
 * Under tab isolation, `userloc` writes go through locationStorage (per-tab + sync).
 * @param {string} cookieName - name of the cookie
 * @param {string} cookieValue - value to store in the cookie
 * @param {number} [days] - the number of days until the cookie expires (optional)
 * @param {string} [path='/'] - the path where the cookie is accessible (optional)
 * @param {string} [domain=''] - the domain where the cookie is valid (optional)
 * @param {boolean} [secure=false] - whether the cookie should be secure (optional)
 * @param {boolean} [httpOnly=false] - whether the cookie should be HttpOnly (optional)
 */
function setCookie(cookieName, cookieValue, days, path = '/', domain = '', secure = false, httpOnly = false) {
    if (cookieName === 'userloc' && tabIdentity.isTabIsolation()) {
        return locationStorage.set(cookieValue);
    }

    let expires = '';

    if (days) {
        let date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000)); // Convert days to milliseconds
        expires = `; expires=${date.toUTCString()}`;
    }

    let cookieParts = [
        `${cookieName}=${encodeURIComponent(cookieValue)}`, expires, `; path=${path}`,
    ];

    if (domain) {
        cookieParts.push(`; domain=${domain}`);
    }

    if (secure) {
        cookieParts.push('; secure');
    }

    if (httpOnly) {
        cookieParts.push('; HttpOnly');
    }

    document.cookie = cookieParts.join('');
}

/**
 * formats number to Au Dollar string with symbol
 * @param {number | string | number | bigint } - value
 * @param {string} - formatted Money string
 */
var formatToAUDollar = new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
}).format;

/**
 * formats number to Au Dollar string with symbol
 * @param {number | string | number | bigint } - value
 * @param {string} - formatted Money string
 */
function formatCurrency(value) {
    let formattedValue = value.replace('$', '').replace('A', '').trim();
    let numericValue = parseFloat(formattedValue.replace(/,/g, ''));
    let roundedValue = Math.round(numericValue);
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: roundedValue % 1 === 0 ? 0 : 2, // No decimals if whole number
        maximumFractionDigits: 2
    }).format(roundedValue);
}

/**
 * Moves contents from one container to another.
 *
 * @param {string} fromId - The ID of the source container.
 * @param {string} toId - The ID of the target container.
 * @param {boolean} [retainOriginal=false] - Whether to retain the original container's contents (default is false, meaning the source will be emptied).
 */
function moveContents(fromId, toId, retainOriginal = false) {
    $(`#${toId}`).append($(`#${fromId}`).children());

    if (!retainOriginal) {
        $(`#${fromId}`).empty();
    }
}


/**
 * Updates the URL parameters in the browser's address bar.
 * It merges the provided object with existing URL parameters,
 * adding new ones or updating existing ones.
 *
 * @param {object} newParams - An object containing the key-value pairs
 * to be added or updated in the URL.
 * Example: {'state': 'VLC', 'pin': '1234', 'pid, 'RS383'}
 * @param {boolean} [usePushState=false] - Optional. If true, uses pushState
 * to add a new history entry. Defaults to false (using replaceState).
 *
 * Example usage:
 * updateUrlParams({'pid': 'RS383'}, true|false);
 * URL becomes: https://example.com/path?pid=RS383
 * Back button will go to: https://example.com/path?pid=RS383 (in this example)
 */
function updateUrlParams(newParams, usePushState = false) {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);

    for (const key in newParams) {
        if (Object.hasOwnProperty.call(newParams, key)) {
            params.set(key, newParams[key]);
        }
    }

    currentUrl.search = params.toString();
    const newUrlString = currentUrl.toString();

    if (usePushState) {
        window.history.pushState(null, '', newUrlString);
    } else {
        window.history.replaceState(null, '', newUrlString);
    }
}


/**
 * Updates the URL parameters in the browser's address bar by deleting specified keys.
 * It takes a state control function (like the setter from useState, although it's not directly used internally)
 * and a comma-separated string of keys to remove from the URL.
 *
 * @param {string} keysToDelete - A comma-separated string of URL parameter keys to delete.
 * Example: 'state,pin,q'
 * @param {boolean} [usePushState=false] - Optional. If true, uses pushState
 * to add a new history entry. Defaults to false (using replaceState).
 */
function deleteUrlParams(keysToDelete, usePushState = false) {
    const currentUrl = new URL(window.location.href);
    const params = new URLSearchParams(currentUrl.search);
    const keysArray = keysToDelete.split(',').map(key => key.trim());

    keysArray.forEach(keyToDelete => {
        if (params.has(keyToDelete)) {
            params.delete(keyToDelete);
        }
    });

    currentUrl.search = params.toString();
    const newUrlString = currentUrl.toString();

    if (usePushState) {
        window.history.pushState(null, '', newUrlString);
    } else {
        window.history.replaceState(null, '', newUrlString);
    }
}


/**
 * Observes changes in one div and syncs the content to another div.
 * @param {string} sourceDivId - The ID of the div to observe for changes.
 * @param {string} targetDivId - The ID of the div to update when the source changes.
 */
function syncDivsOnChange(sourceDivId, targetDivId) {
    const sourceDiv = document.getElementById(sourceDivId);
    const targetDiv = document.getElementById(targetDivId);
    if (!sourceDiv || !targetDiv) return;

    // Initial sync
    targetDiv.innerHTML = sourceDiv.innerHTML;

    const observer = new MutationObserver(() => {
        targetDiv.innerHTML = sourceDiv.innerHTML;
    });

    observer.observe(sourceDiv, {
        childList: true,
        subtree: true,
        characterData: true
    });
}

/**
 * Delete a cookie by name from the browser.
 * Under tab isolation, `userloc` deletes go through locationStorage (per-tab + cookie).
 *
 * @param {string} name - name of the cookie
 */
function deleteCookie(name) {
    if (name === 'userloc' && tabIdentity.isTabIsolation()) {
        return locationStorage.remove();
    }

    document.cookie = name + '=; Max-Age=0; path=/;';
}

/**
 * Converts form HTML element to JSON object with all field values.
 * Pure JavaScript implementation - handles text inputs, email, tel, number, password, checkbox, radio, select, and textarea.
 * Skips date, time, and file input types.
 *
 * @param {HTMLFormElement} formElement - The form DOM element
 * @returns {Object} JSON object with field names as keys and field values as values
 *
 * Example usage:
 * const formData = formToJson(document.querySelector('form'));
 * const formData = formToJson(document.getElementById('myForm'));
 */
function formToJson(formElement) {
    if (!formElement || formElement.tagName !== 'FORM') {
        console.warn('formToJson: Invalid form element provided');
        return {};
    }

    var jsonObj = {};
    var formData = new FormData(formElement);

    for (var pair of formData.entries()) {
        var name = pair[0];
        var value = pair[1];

        // Skip if value is empty string
        if (value === '') {
            continue;
        }

        // If key already exists, convert to array or append to array
        if (jsonObj.hasOwnProperty(name)) {
            if (Array.isArray(jsonObj[name])) {
                jsonObj[name].push(value);
            } else {
                jsonObj[name] = [jsonObj[name], value];
            }
        } else {
            jsonObj[name] = value;
        }
    }

    return jsonObj;
}


/**
 * Generates a UUID v4. Falls back to Math.random for Safari <15.4.
 * @returns {string}
 */
function generateUUID() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
        return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        var r = Math.random() * 16 | 0;
        var v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Determines whether a given HEX color is considered "light"
 * based on its relative luminance.
 *
 * Accepts short HEX (#abc) or full HEX (#aabbcc).
 *
 * @param {string} hex - The hex color string (e.g., "#fff", "#000000", "#c33").
 * @returns {boolean} Returns true if the color is light, false if dark.
 */

function isHexLight(hex) {
    hex = hex.replace('#', '').trim();
    if (hex.length === 3) {
        hex = hex.split('').map(c => c + c).join('');
    }
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5;
}

/**
 * SHA-256 hash a string (returns a promise resolving to 64-char hex).
 * Delegates to analyticsHelper — single implementation across all integrations.
 * @param {string} str
 * @returns {Promise<string|null>}
 */
var sha256 = require('app_honda_cars/utils/analyticsHelper').sha256;

module.exports = {
    BREAKPOINTS: BREAKPOINTS,
    getCookie: getCookie,
    setCookie: setCookie,
    formatToAUDollar: formatToAUDollar,
    formatCurrency: formatCurrency,
    moveContents: moveContents,
    updateUrlParams: updateUrlParams,
    deleteUrlParams: deleteUrlParams,
    syncDivsOnChange: syncDivsOnChange,
    deleteCookie: deleteCookie,
    formToJson: formToJson,
    isHexLight: isHexLight,
    generateUUID: generateUUID,
    sha256: sha256
}
